import { z } from 'zod';

const savedAgentIdSchema = z
  .string()
  .regex(/^[a-zA-Z0-9._-]{1,128}$/)
  .refine((value) => !['__proto__', 'constructor', 'prototype'].includes(value), 'Reserved ID.');

export const crowdexAgentReminderSchema = z
  .object({
    id: savedAgentIdSchema,
    trigger: z.enum(['before-turn', 'after-handoff', 'workspace-changed']).default('before-turn'),
    content: z.string().trim().min(1).max(4000),
    priority: z.number().int().min(0).max(100).default(50),
  })
  .strict();

export const crowdexAgentPolicySchema = z
  .object({
    /** Maximum approval automation this agent may receive. */
    permissionMode: z.enum(['allow-reads', 'allow-edits', 'allow-all']).default('allow-all'),
  })
  .strict();

export const crowdexAgentCollaborationSchema = z
  .object({
    strategy: z.enum(['solo', 'delegate']).default('solo'),
    handoffTargets: z.array(savedAgentIdSchema).max(32).optional(),
  })
  .strict();

/** Provider-neutral payload for an account-owned reusable agent profile (`agent_profile`). */
export const crowdexSavedAgentSchema = z
  .object({
    id: savedAgentIdSchema,
    name: z.string().trim().min(1).max(80),
    agentId: savedAgentIdSchema,
    animatedAvatarId: savedAgentIdSchema.optional(),
    avatar: z
      .string()
      .max(180000)
      .regex(/^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/=]+$/)
      .optional(),
    mcpServerNames: z.array(z.string().min(1)).max(128).optional(),
    skillRefs: z
      .array(
        z.object({
          plugin: z.string().min(1),
          uri: z.string().startsWith('skill://'),
          name: z.string().min(1),
        }),
      )
      .optional(),
    /** How optional MCP servers and skills are resolved. */
    capabilityMode: z.enum(['inherit', 'custom', 'none']).optional(),
    description: z.string().max(300).optional(),
    policy: crowdexAgentPolicySchema.optional(),
    systemReminders: z.array(crowdexAgentReminderSchema).max(32).optional(),
    collaboration: crowdexAgentCollaborationSchema.optional(),
    instructions: z.string().max(10000).optional(),
    /** Inherit the conversation model or pin this agent to modelId. */
    modelMode: z.enum(['inherit', 'fixed']).optional(),
    modelId: z.string().trim().min(1).max(200).optional(),
  })
  .superRefine((agent, context) => {
    if (agent.modelMode === 'fixed' && agent.modelId === undefined) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['modelId'],
        message: 'A fixed model requires a model ID.',
      });
    }
  });

export type CrowdexSavedAgent = z.infer<typeof crowdexSavedAgentSchema>;
export type CrowdexAgentReminder = z.infer<typeof crowdexAgentReminderSchema>;
export type CrowdexResolvedAgent = Omit<CrowdexSavedAgent, 'id'> & {
  id: string;
  profileId: string;
};
